// Arduino Mega 2560 R3 en 3D.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { MEGA } from '../../parts/boards.js';
import { getPins } from '../../parts/registry.js';
import { rand, femaleHeader, maleHeader2 } from './uno.js';
import { usbCable } from './usb.js';

const W = MEGA.W;
const D = MEGA.D;
const TOP = MEGA.PCB_TOP;
const TEAL = '#0b7d8e';
const MIL = 0.0254;
const X = (xm) => (xm - 2000) * MIL;
const Z = (ym) => -(ym - 1050) * MIL;

let silkTex = null;
let chipTex = null;

function drawSilk(pins) {
  const { c, g } = canvas(W, D, 20);
  const cx = (x) => x + W / 2;
  const cz = (z) => z + D / 2;
  g.fillStyle = TEAL;
  g.fillRect(0, 0, W, D);
  const r = rand(2560);
  g.strokeStyle = 'rgba(4,70,82,0.55)';
  g.lineWidth = 0.28;
  g.lineCap = 'round';
  for (let i = 0; i < 110; i++) {
    let x = 4 + r() * (W - 8);
    let y = 4 + r() * (D - 8);
    g.beginPath();
    g.moveTo(x, y);
    for (let k = 0; k < 3; k++) {
      const len = 3 + r() * 14;
      if (r() > 0.5) x += r() > 0.5 ? len : -len;
      else y += r() > 0.5 ? len : -len;
      x = Math.max(2, Math.min(W - 2, x));
      y = Math.max(2, Math.min(D - 2, y));
      g.lineTo(x, y);
    }
    g.stroke();
  }
  const white = '#f3f6f6';
  g.fillStyle = white;
  g.strokeStyle = white;
  g.lineWidth = 0.18;
  for (const p of pins) {
    const n = Number(p.name);
    let label = p.name;
    if (p.name.startsWith('GND')) label = 'GND';
    else if (p.name.startsWith('5V')) label = '5V';
    else if (p.name === '3.3V') label = '3.3V';
    else if (p.name === 'VIN') label = 'Vin';
    else if (Number.isFinite(n) && [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(n)) label = '~' + n;
    if (p.x > X(3600)) {
      // Doble fila: etiquetas a los lados
      text(g, label, cx(p.x) + (p.x > X(3750) ? 1.9 : -1.9), cz(p.z), { size: 0.95, color: white, weight: 700, align: p.x > X(3750) ? 'left' : 'right' });
    } else if (p.z < 0) {
      text(g, label, cx(p.x), cz(p.z) + 2.0, { size: 1.05, color: white, align: 'right', rotate: -Math.PI / 2, weight: 700 });
    } else {
      text(g, label, cx(p.x), cz(p.z) - 2.0, { size: 1.05, color: white, align: 'left', rotate: -Math.PI / 2, weight: 700 });
    }
  }
  text(g, 'PWM', cx(X(2200)), cz(Z(1700)), { size: 1.5, color: white, weight: 700 });
  text(g, 'COMMUNICATION', cx(X(3090)), cz(Z(1700)), { size: 1.5, color: white, weight: 700 });
  text(g, 'POWER', cx(X(1350)), cz(Z(420)), { size: 1.4, color: white, weight: 700 });
  text(g, 'ANALOG IN', cx(X(2700)), cz(Z(420)), { size: 1.4, color: white, weight: 700 });
  text(g, 'DIGITAL', cx(X(3750)), cz(Z(2080)) - 0.2, { size: 1.0, color: white, weight: 700 });
  // Logotipo
  const lx = cx(X(1450));
  const ly = cz(Z(1250));
  g.lineWidth = 0.85;
  g.beginPath();
  g.ellipse(lx - 2.9, ly, 2.4, 2.0, 0, 0, Math.PI * 2);
  g.ellipse(lx + 2.9, ly, 2.4, 2.0, 0, 0, Math.PI * 2);
  g.stroke();
  g.fillRect(lx - 4.0, ly - 0.28, 2.2, 0.56);
  g.fillRect(lx + 1.8, ly - 0.28, 2.2, 0.56);
  g.fillRect(lx + 2.62, ly - 1.1, 0.56, 2.2);
  text(g, 'ARDUINO', lx, ly + 3.6, { size: 1.8, color: white, weight: 800 });
  text(g, 'MEGA 2560', cx(X(1450)), cz(Z(820)), { size: 3.4, color: white, weight: 800 });
  text(g, 'L', cx(-36.2), cz(-14.2), { size: 1.1, color: white, weight: 700 });
  text(g, 'TX', cx(-36.6), cz(-11.7), { size: 1.1, color: white, weight: 700 });
  text(g, 'RX', cx(-36.6), cz(-9.5), { size: 1.1, color: white, weight: 700 });
  text(g, 'ON', cx(X(3420)), cz(Z(1520)), { size: 1.1, color: white, weight: 700 });
  text(g, 'RESET', cx(-37.6), cz(-17.6), { size: 1.0, color: white, weight: 700 });
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

function megaChip() {
  const { c, g } = canvas(14, 14, 20);
  g.fillStyle = '#1b1b1e';
  g.fillRect(0, 0, 14, 14);
  text(g, 'ATMEGA2560', 7, 6.2, { size: 1.3, color: '#9a9ca1', weight: 600 });
  text(g, '16AU  1838', 7, 8.2, { size: 0.9, color: '#7c7e83', weight: 500 });
  g.fillStyle = '#111113';
  g.beginPath();
  g.arc(2, 12, 0.6, 0, Math.PI * 2);
  g.fill();
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

export function buildMega(def, props) {
  const pins = getPins(def.type, props);
  if (!silkTex) silkTex = drawSilk(pins);
  if (!chipTex) chipTex = megaChip();
  const b = new Builder();
  const holes = [[X(550), Z(100), 1.6], [X(600), Z(2000), 1.6], [X(2600), Z(1400), 1.6], [X(3550), Z(600), 1.6], [X(3550), Z(2000), 1.6]];
  const pcb = extrudeBoard(boardShape(W, D, { r: 1.2, chamfer: { tr: 2.4, br: 2.4 } }), MEGA.PCB_T, silkTex, '#0a6d7c', { holes, w: W, d: D, y0: MEGA.PCB_Y });
  b.root.add(pcb);
  for (const [x, z] of holes) {
    b.add(new THREE.RingGeometry(1.6, 2.7, 24).rotateX(-Math.PI / 2), M.tin, { x, y: TOP + 0.02, z });
    b.cyl(2.6, 2.6, MEGA.PCB_Y, M.black, x, 0, z, 16);
  }
  const byName = Object.fromEntries(pins.map((p) => [p.name, p]));
  const xs = (names) => names.map((n) => byName[n].x);
  femaleHeader(b, xs(['SCL', 'SDA', 'AREF', 'GND.1', '13', '12', '11', '10', '9', '8']), byName['8'].z, TOP);
  femaleHeader(b, xs(['7', '6', '5', '4', '3', '2', '1', '0']), byName['0'].z, TOP);
  femaleHeader(b, xs(['14', '15', '16', '17', '18', '19', '20', '21']), byName['14'].z, TOP);
  femaleHeader(b, [byName.IOREF.x - 2.54, ...xs(['IOREF', 'RESET', '3.3V', '5V', 'GND.2', 'GND.3', 'VIN'])], byName.VIN.z, TOP);
  femaleHeader(b, xs(['A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7']), byName.A0.z, TOP);
  femaleHeader(b, xs(['A8', 'A9', 'A10', 'A11', 'A12', 'A13', 'A14', 'A15']), byName.A8.z, TOP);
  // Doble fila de 36 pines (dos tiras de 18 en vertical)
  for (const col of [3700, 3800]) {
    const x = X(col);
    const z0 = Z(2000);
    const z1 = Z(300);
    b.box(2.54, 8.5, z1 - z0 + 2.54, M.blackPlastic, x, TOP, (z0 + z1) / 2);
    for (let ym = 300; ym <= 2000; ym += 100) {
      b.box(1.05, 0.06, 1.05, M.hole, x, TOP + 8.5, Z(ym));
      b.box(1.5, 0.04, 1.5, mat('#26272a', { roughness: 0.5 }), x, TOP + 8.48, Z(ym));
    }
  }
  // USB, alimentación, ATmega2560 (a 45°), 16U2, cristal, regulador y capacitores
  const left = -W / 2;
  b.box(16, 10.9, 12, M.metal, left + 3.8, TOP, -11.43);
  b.box(0.2, 7.6, 8.4, M.hole, left - 4.2, TOP + 1.6, -11.43);
  b.box(4, 3.2, 5.6, mat('#e9e7df', { roughness: 0.6 }), left - 2.3, TOP + 3.8, -11.43);
  b.box(13.5, 11, 9, M.black, left + 5.7, TOP, 18.4);
  b.add(new THREE.CylinderGeometry(3.2, 3.2, 0.4, 24), M.hole, { x: left - 1.1, y: TOP + 6.2, z: 18.4, rz: Math.PI / 2 });
  const chipX = X(2550);
  const chipZ = Z(1050);
  b.box(14, 1.4, 14, M.chip, chipX, TOP, chipZ, { ry: Math.PI / 4 });
  for (let side = 0; side < 4; side++) {
    for (let i = 0; i < 25; i++) {
      const t = (i - 12) * 0.5;
      const a = Math.PI / 4 + (side * Math.PI) / 2;
      const ox = Math.cos(a) * 7.6 - Math.sin(a) * t;
      const oz = Math.sin(a) * 7.6 + Math.cos(a) * t;
      b.box(1.2, 0.25, 0.22, M.tin, chipX + ox, TOP, chipZ + oz, { ry: -a });
    }
  }
  const lbl = new THREE.Mesh(new THREE.PlaneGeometry(13.6, 13.6).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: chipTex, roughness: 0.55 }));
  lbl.rotation.y = Math.PI / 4;
  lbl.position.set(chipX, TOP + 1.42, chipZ);
  b.root.add(lbl);
  b.box(5, 0.9, 5, M.chip, left + 18.3, TOP, -10.9, { ry: Math.PI / 4 });
  b.rbox(11, 3.6, 4.6, 1.6, M.steel, left + 22.8, TOP, -2.6);
  b.rbox(8, 3.0, 3.4, 1.2, M.metal, X(2050), TOP, Z(1300));
  b.box(6.5, 1.7, 3.6, M.chip, left + 12.9, TOP, 12.4);
  for (const x of [left + 17.7, left + 23.8]) {
    b.cyl(3.15, 3.15, 5.6, mat('#1d2a3d', { roughness: 0.5 }), x, TOP, 19.6, 24);
    b.cyl(3.0, 3.0, 0.3, M.metal, x, TOP + 5.6, 19.6, 24);
  }
  maleHeader2(b, X(3370), Z(1050), 2, 3, TOP, { rotate: true });
  maleHeader2(b, left + 31.8, -17, 2, 3, TOP, { rotate: true });
  const r = rand(77);
  const smd = [mat('#2b2722', { roughness: 0.5 }), mat('#b8a07a', { roughness: 0.6 }), M.chip];
  for (let i = 0; i < 40; i++) {
    const x = -40 + r() * 75;
    const z = -9 + r() * 22;
    if (Math.hypot(x - chipX, z - chipZ) < 13) continue;
    b.box(1.6, 0.55, 0.8, smd[i % 3], x, TOP, z, { ry: r() > 0.5 ? Math.PI / 2 : 0 });
  }
  b.box(6, 2.2, 6, M.metal, left + 8.4, TOP, -21.6);
  b.box(4.6, 0.6, 4.6, M.black, left + 8.4, TOP + 2.2, -21.6);
  b.build();

  const cap = new THREE.Mesh(new THREE.CylinderGeometry(1.9, 1.9, 1.5, 24), mat('#c43a2f', { roughness: 0.45 }));
  cap.position.set(left + 8.4, TOP + 3.55, -21.6);
  cap.castShadow = true;
  cap.userData.interactive = { kind: 'press', key: 'reset', label: 'RESET' };
  b.root.add(cap);

  // Cable USB (prop «usb»)
  const usb = usbCable('B', left - 4.2, TOP + 5.45, -11.43, [16, 10.9, 12]);
  b.root.add(usb.group, usb.hit);
  usb.group.visible = props.usb !== false;

  const leds = {};
  const ledDefs = [
    ['L', left + 28.2, -14.2, '#ffb02e', '#ffa31a'],
    ['TX', left + 28.2, -11.7, '#ffd84a', '#ffcc1a'],
    ['RX', left + 28.2, -9.5, '#ffd84a', '#ffcc1a'],
    ['ON', X(3420), Z(1430), '#7dff8a', '#36ff4a'],
  ];
  for (const [name, x, z, base, gcol] of ledDefs) {
    const m = emissiveMat('#f2efe6', gcol, { roughness: 0.3 });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.8, 1.25), m);
    mesh.position.set(x, TOP + 0.4, z);
    b.root.add(mesh);
    const halo = glow(gcol, 9);
    halo.position.set(x, TOP + 1.6, z);
    b.root.add(halo);
    leds[name] = { m, halo, base };
  }
  const view = {
    root: b.root,
    update(v) {
      const set = (name, on) => {
        const l = leds[name];
        setEmissive(l.m, on, 14);
        l.m.color.set(on > 0.05 ? l.base : '#f2efe6');
        setGlow(l.halo, on * 0.9);
      };
      set('L', v.L || 0);
      set('TX', v.TX ? 1 : 0);
      set('RX', v.RX ? 1 : 0);
      set('ON', 0.8 * (v.ON === true ? 1 : Number(v.ON) || 0));
    },
    reset() {
      view.update({});
    },
    press(key, down) {
      if (key === 'reset') cap.position.y = TOP + (down ? 3.05 : 3.55);
    },
    setProps(p) {
      usb.group.visible = p.usb !== false;
    },
  };
  view.reset();
  return view;
}
